<template>
  <div class="tw-min-h-[calc(100vh-60px)] tw-bg-gradient-to-br tw-from-slate-50 tw-via-white tw-to-indigo-50 tw-py-6">
    <div class="tw-mx-auto tw-w-full tw-max-w-7xl tw-px-4 sm:tw-px-6 lg:tw-px-8">
      <div v-if="mainLoading" class="tw-flex tw-justify-center tw-py-16">
        <div class="tw-h-10 tw-w-10 tw-animate-spin tw-rounded-full tw-border-4 tw-border-slate-200 tw-border-t-slate-600"></div>
      </div>
      <div v-else class="tw-w-full">
        <Modal v-model:isOpen="isContainerModalOpen" modalId="archived-container-modal" maxWidth="7xl">
          <template #title>
            <div class="tw-text-base sm:tw-text-lg tw-font-semibold tw-text-slate-900 tw-mr-4  tw-inline-block">
              <CopyButton
                v-if="currentContainerFromList"
                :value="String(currentContainerFromList.containerId)"
                mode="wrap-content"
                title="Copy container ID"
                aria-label="Copy container ID to clipboard"
              >
                {{ currentContainerFromList.containerId }}
              </CopyButton>
              <span v-else>Container</span>
            </div>
            <div class="tw-text-sm tw-text-slate-600  tw-inline-block">
              <span v-if="currentContainerFromList?.billOfLading">
                BL#
                <CopyButton
                  :value="currentContainerFromList.billOfLading.billOfLadingNumber"
                  mode="wrap-content"
                  title="Copy B/L number"
                  aria-label="Copy B/L number to clipboard"
                >
                  {{ currentContainerFromList.billOfLading.billOfLadingNumber }}
                </CopyButton>
              </span>
              <span v-if="currentContainerFromList?.bookingNumber">
                Booking #
                <CopyButton
                  :value="currentContainerFromList.bookingNumber"
                  mode="wrap-content"
                  title="Copy Booking number"
                  aria-label="Copy Booking number to clipboard"
                >
                  {{ currentContainerFromList.bookingNumber }}
                </CopyButton>
              </span>
            </div>
          </template>
          <template #content>
            <div class="tw--m-5">
              <ContainerModal
                :containerId="currentContainerId"
                :containerFromList="currentContainerFromList"
                v-bind:enableVessel="false"
                @close="closeContainerModal"
                @unarchiveContainer="unarchiveContainer"
              ></ContainerModal>
            </div>
          </template>
        </Modal>

        <AddNotesModal
          :open="containerNotesModalOpen"
          title="Container notes"
          :subtitle="containerNotesSelected ? String(containerNotesSelected.containerId) : undefined"
          :container="containerNotesSelected"
          @close="closeContainerNotes"
          @addedNote="onContainerNotesAdded"
          @archivedNotesUpdated="onContainerArchivedNotesUpdated"
        />

        <div class="tw-mb-4 tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
          <div class="tw-text-left">
            <h3 class="tw-text-xl tw-font-semibold tw-text-slate-900">Archived Containers</h3>
            <div class="tw-mt-1 tw-text-sm tw-text-slate-600">Search, review, and unarchive containers.</div>
          </div>

          <div class="tw-w-full sm:tw-w-[520px]">
            <input
              class="tw-w-full tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-900/10"
              type="search"
              placeholder="Search by Container Number, B/L Number, or Customer Name"
              v-model="searchInput"
              @input="onSearchInput"
            />
          </div>
        </div>

        <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-200/50">
          <div v-if="tableLoading" class="tw-flex tw-items-center tw-justify-center tw-py-10">
            <div class="tw-h-8 tw-w-8 tw-animate-spin tw-rounded-full tw-border-4 tw-border-slate-200 tw-border-t-slate-600"></div>
          </div>

          <div
            v-else-if="archivedContainersEnumerated.length === 0"
            class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-px-6 tw-py-12"
          >
            <div class="tw-text-base tw-font-semibold tw-text-slate-900">No archived containers</div>
            <div class="tw-max-w-md tw-text-center tw-text-sm tw-text-slate-600">
              Try adjusting your search term, or check back later if you recently archived something.
            </div>
          </div>

          <div v-else>
            <ContainerTable
              @refreshContainers="refreshContainers"
              @unarchiveContainer="unarchiveContainer"
              :containers="archivedContainersEnumerated"
              :condensed="user.enableCustomerInfo != true"
              :markedUpdatedContainers="markedUpdatedContainers"
              @selectContainer="selectArchivedContainer"
              @openContainerNotes="openContainerNotes"
              :sortBy="sortBy"
              @updateSortBy="updateSortBy"
            ></ContainerTable>
          </div>
        </div>

        <div class="tw-mt-6 tw-flex tw-justify-center">
          <PaginationBar
            :page="pageNum"
            :totalPages="pageCount || 1"
            :disabled="tableLoading"
            @previousPage="prevPage"
            @nextPage="nextPage"
            @goToPage="goToPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { pushToast } from '@/lib/toast';
import ArchiveContainer from '@/gql/containers/ArchiveContainer.gql';
import UnarchiveContainer from '@/gql/containers/UnarchiveContainer.gql';
import ALL_CONTAINERS from '../../gql/containers/Containers.gql';
import ArchivedContainersQuery from '../../gql/containers/ArchivedContainers.gql';
import ActiveContainersQuery from '../../gql/containers/ActiveContainers.gql';
import StartContainerRescrapeFromTerminal from '../../gql/StartContainerRescrapeFromTerminal.gql';
import ContainerTable from './ContainerTable.vue';
import ContainerModal from './ContainerModal.vue';
import Modal from '@/components/core/Modal/Modal.vue';
import CopyButton from '@/components/core/CopyButton.vue';
import AddNotesModal from '@/components/core/AddNotesModal.vue';
import Customers from '../../gql/customers/Customers.gql';
import activeContainersWithoutOrderQuery from '../../gql/orders/queries/ActiveContainersWithoutOrderQuery.gql';
import ActiveBLs from '../../gql/orders/ActiveBLs.gql';
import YardStatusLabel from '../UI/YardStatusLabel.vue';

import VesselsQuery from '../../gql/orders/Vessels.gql';
import { K_SORT_BY } from '../../types';
import PaginationBar from '@/components/core/PageElements/PaginationBar.vue';

type User = {
  activeContainers: [];
  archivedContainers: [];
  onboardedPayments: boolean;
  onboardedTerminalLogins: boolean;
  firstName: string;
};

function enumerateContainers(containers) {
  return containers.map((cntnr, idx) => {
    return { container: cntnr, index: idx };
  });
}

export default defineComponent({
  name: 'ArchivedContainerTableView',
  components: {
    ContainerModal,
    Modal,
    CopyButton,
    AddNotesModal,
    ContainerTable,
    PaginationBar,
  },
  updated() {
    if ((this as any).$route.params.containerId) {
      (this as any).$nextTick(() => {
        (this as any).selectArchivedContainer(
          Number((this as any).$route.params.containerId),
        );
      });
    }
  },
  computed: {
    isOnboardedPayments(): any {
      return (this as any).user.onboardedPayments;
    },
    archivedContainersEnumerated(): any[] {
      return enumerateContainers((this as any).archivedContainers);
    },

    isSearching(): boolean {
      return ((this as any).searchTerm as string).length > 0;
    },

  },
  data(): any {
    return {
      user: {} as User,
      archivedContainers: [],
      selectedContainer: -1,
      loading: [],
      markedUpdatedContainers: [] as any[],
      searchInput: '',
      searchTerm: '',
      searchDebounceTimer: null as ReturnType<typeof setTimeout> | null,
      sortBy: 'eta',
      currentContainerId: -1,
      currentContainerFromList: null,
      isContainerModalOpen: false,
      mainLoading: true,
      tableLoading: false,

      pageNum: 0,
      pageSize: 25,
      pageCount: 0,
      totalCount: 0,
      containerNotesModalOpen: false,
      containerNotesSelected: null as any,
    };
  },
  methods: {
    prevPage() {
      const vm = (this as any);
      if (vm.tableLoading) return;
      if (vm.pageNum > 0) {
        vm.pageNum -= 1;
      }
    },
    nextPage() {
      const vm = (this as any);
      if (vm.tableLoading) return;
      if ((vm.pageCount || 0) <= 0) return;
      if (vm.pageNum + 1 < vm.pageCount) {
        vm.pageNum += 1;
      }
    },
    goToPage(idx: number) {
      const vm = (this as any);
      if (vm.tableLoading) return;
      if (idx < 0) return;
      if (idx === vm.pageNum) return;
      if ((vm.pageCount || 0) > 0 && idx > vm.pageCount - 1) return;
      vm.pageNum = idx;
    },
    updateSortBy(val: string) {
      if (val) {
        (this as any).sortBy = val;
      }
    },
    onSearchInput() {
      const vm = (this as any);

      if (vm.searchDebounceTimer) {
        clearTimeout(vm.searchDebounceTimer);
      }

      vm.searchDebounceTimer = setTimeout(() => {
        if (vm.searchTerm !== vm.searchInput) {
          vm.searchTerm = vm.searchInput;
          vm.pageNum = 0;
        }
      }, 350);
    },

    updateUser() {
      this.$apollo.queries.user.refetch();
    },
    // seems orphaned
    // updateUserMarkContainers(updatedContainers: any) {
    //   console.log('marking updated containers');
    //   console.log(updatedContainers);
    //   (this as any).markedUpdatedContainers = updatedContainers;
    //   this.$apollo.queries.user.refetch();
    //   this.$apollo.queries.activeBls.refetch();
    // },
    selectArchivedContainer(containerIndex: number) {
      const vm = (this as any);
      vm.currentContainerId = containerIndex;
      vm.currentContainerFromList = vm.archivedContainers.find(
        (container) => container.id === containerIndex,
      ) || null;
      (this as any).isContainerModalOpen = true;
    },
    openContainerNotes(container: any) {
      const vm = this as any;
      vm.containerNotesSelected = container;
      vm.containerNotesModalOpen = true;
    },
    closeContainerNotes() {
      const vm = this as any;
      vm.containerNotesModalOpen = false;
      vm.containerNotesSelected = null;
    },
    onContainerNotesAdded(notes: any[]) {
      const vm = this as any;
      const id = vm.containerNotesSelected?.id;
      if (id == null) return;
      const nextNotes = Array.isArray(notes) ? notes : [];
      const idNum = Number(id);
      if (Array.isArray(vm.archivedContainers)) {
        vm.archivedContainers = vm.archivedContainers.map((c: any) =>
          Number(c.id) === idNum ? { ...c, notes: nextNotes } : c,
        );
      }
      if (vm.containerNotesSelected && Number(vm.containerNotesSelected.id) === idNum) {
        vm.containerNotesSelected = { ...vm.containerNotesSelected, notes: nextNotes };
      }
    },
    onContainerArchivedNotesUpdated(archivedNotes: any[]) {
      const vm = this as any;
      const id = vm.containerNotesSelected?.id;
      if (id == null) return;
      const nextArchived = Array.isArray(archivedNotes) ? archivedNotes : [];
      const idNum = Number(id);
      if (Array.isArray(vm.archivedContainers)) {
        vm.archivedContainers = vm.archivedContainers.map((c: any) =>
          Number(c.id) === idNum ? { ...c, archivedNotes: nextArchived } : c,
        );
      }
      if (vm.containerNotesSelected && Number(vm.containerNotesSelected.id) === idNum) {
        vm.containerNotesSelected = { ...vm.containerNotesSelected, archivedNotes: nextArchived };
      }
    },
    closeContainerModal() {
      const vm = (this as any);
      try {
        const path = vm.$router && vm.$router.currentRoute && vm.$router.currentRoute.path;
        if (path && path.includes('/archived') && path !== '/archived/') {
          vm.$router.replace('/archived/');
        }
      } catch (e) {
      }
      vm.isContainerModalOpen = false;
      vm.currentContainerId = -1;
      vm.currentContainerFromList = null;
    },
    refreshContainerStatus(containerIndex: any) {
      const container: any = (this as any).user.activeContainers[
        containerIndex
      ];
      (this as any).loading.push(containerIndex);
      console.log('requesting container update', container);
      this.$apollo
        .mutate({
          mutation: StartContainerRescrapeFromTerminal,
          variables: {
            containerId: container.containerId,
          },
        })
        .then((data) => {
          if ((this as any).loading.includes(containerIndex)) {
            (this as any).loading = (this as any).loading.filter(
              (idx) => idx !== containerIndex,
            );
          }
          this.$apollo.queries.user.refetch();
          console.log(data);
        });
    },
    refreshContainers() {
      this.$apollo.queries.user.refetch();
    },
    unarchiveContainer(containerId: number) {
      this.$apollo
        .mutate({
          mutation: UnarchiveContainer,
          variables: {
            containerId,
          },
          update: (store, { data }) => {
            try {
              const archivedStore = store.readQuery({
                query: ArchivedContainersQuery,
              }) as any;

              let archivedContainers = Array.isArray(
                archivedStore && archivedStore.archivedContainers
              )
                ? archivedStore.archivedContainers.slice()
                : [];

              const exists = archivedContainers.some((c) => c.id === containerId);
              if (exists) {
                archivedContainers = archivedContainers.filter(
                  (x) => x.id !== containerId,
                );
              }

              const activeContainersStore = (store.readQuery({
                query: ALL_CONTAINERS,
              }) as any) || { activeContainers: [] };

              const activeContainers = Array.isArray(activeContainersStore.activeContainers)
                ? activeContainersStore.activeContainers.filter((x) => x.id !== containerId)
                : [];

              if (data && data.unarchiveContainer && data.unarchiveContainer.container) {
                activeContainers.push(data.unarchiveContainer.container);
              }

              store.writeQuery({
                query: ArchivedContainersQuery,
                data: {
                  archivedContainers,
                },
              });

              store.writeQuery({
                query: ALL_CONTAINERS,
                data: {
                  activeContainers,
                },
              });
            } catch (e) {
              console.warn('Could not update Apollo cache for unarchiveContainer', e);
            }
          },
        })
        .then(() => {
          this.$apollo.queries.archivedContainers.refetch();
          pushToast(`Unarchived Container ${containerId}`, 'success', 5000);
          try {
            const path = this.$router && this.$router.currentRoute && this.$router.currentRoute.path;
            if (path && path.includes('/archived') && path !== '/archived/') {
              this.$router.replace('/archived/');
            }
          } catch (e) {
            // defensive: router shape unexpected, ignore
          }
        });
    },
  },
  props: {
    msg: String,
  },
  apollo: {
    archivedContainers: {
      query: ArchivedContainersQuery,
      variables() {
        return {
          pageNum: (this as any).pageNum,
          pageSize: (this as any).pageSize,
          searchQuery: (this as any).searchTerm,
        };
      },
      debounce: 0,
      update(data: any) {
        if (data && data.archivedContainers && Array.isArray(data.archivedContainers.containers)) {
          return data.archivedContainers.containers;
        }
        return [];
      },
      watchLoading(isLoading) {
        (this as any).tableLoading = isLoading;
      },
      result(result) {
        (this as any).mainLoading = false;
        const vm = (this as any);
        const payload = result && result.data ? result.data.archivedContainers : null;
        vm.pageCount = payload && typeof payload.pages === 'number' ? payload.pages : 0;
        vm.totalCount = payload && typeof payload.total === 'number' ? payload.total : 0;
      },
      error(err) {
        console.error(err);
      },
    },
  },
});
</script>
